import React from 'react';
import { ApolloProvider, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { IntlProvider } from 'react-intl';
import client from '../../lib/apolloClient';
import translations from '../../lib/i18n/translations.json';
import SuccessionThemeProvider from '../../components/Succession/SuccessionThemeProvider';
import BuyerReadinessAssessment from '../../components/Succession/BuyerReadinessAssessment/BuyerReadinessAssessment';

// ---------------------------------------------------------------------------
// GraphQL queries
// ---------------------------------------------------------------------------

const GET_ASSESSMENT_DATA = gql`
  query GetBuyerAssessmentData($keyPrefix: String!) {
    assessmentQuestions(key_prefix: $keyPrefix) {
      id
      key
      text
      category
      scale_min
      scale_max
      scale_min_label
      scale_max_label
    }
    getCurrentUser {
      id
      name
    }
  }
`;

const GET_COMPANY_FEATURES = gql`
  query GetCompanyFeatures {
    getCurrentUser {
      id
      company {
        id
        features
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Inner wrapper — resolves the feature flag, then fetches questions
// ---------------------------------------------------------------------------

function BuyerReadinessAssessmentWithData({ company }) {
  const locale = window.locale || 'en';

  // Determine which assessment type from company features
  const features = company?.features || [];
  const hasFull = features.includes('buyer_readiness_assessment_full');
  const hasQuick = features.includes('buyer_readiness_assessment_quick');

  // Default to full if both are somehow on; fall back to quick
  let assessmentType = 'buyer_full';
  let keyPrefix = 'buyer_full_';
  if (hasQuick && !hasFull) {
    assessmentType = 'buyer_quick';
    keyPrefix = 'buyer_quick_';
  }

  const { data, loading, error } = useQuery(GET_ASSESSMENT_DATA, {
    variables: { keyPrefix },
    fetchPolicy: 'cache-and-network',
  });

  const questions = data?.assessmentQuestions || [];

  return (
    <IntlProvider locale={locale} messages={translations[locale] || translations.en}>
      <SuccessionThemeProvider company={company}>
        <BuyerReadinessAssessment
          questions={questions}
          assessmentType={assessmentType}
          loading={loading}
          error={error}
        />
      </SuccessionThemeProvider>
    </IntlProvider>
  );
}

// ---------------------------------------------------------------------------
// Outer container — provides Apollo and resolves company data
// ---------------------------------------------------------------------------

function BuyerReadinessAssessmentInner(props) {
  // If company was passed via Rails props, use it directly
  if (props.company && props.company.features) {
    return <BuyerReadinessAssessmentWithData company={props.company} />;
  }

  // Otherwise fetch company features via GraphQL
  return <CompanyFeaturesResolver {...props} />;
}

function CompanyFeaturesResolver(props) {
  const { data, loading } = useQuery(GET_COMPANY_FEATURES, {
    fetchPolicy: 'cache-and-network',
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-succession-bg-secondary flex items-center justify-center">
        <div className="inline-block w-8 h-8 border-2 border-succession-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const company = data?.getCurrentUser?.company || props.company || {};
  // Merge features from props if available
  const mergedCompany = {
    ...company,
    features: company.features || props.features || [],
  };

  return <BuyerReadinessAssessmentWithData company={mergedCompany} />;
}

// ---------------------------------------------------------------------------
// ReactOnRails entry point
// ---------------------------------------------------------------------------

const BuyerReadinessAssessmentContainer = (props, railsContext) => {
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';

  const mergedCompany = props.company
    ? {
        ...props.company,
        features: props.company.features || props.features || [],
      }
    : {
        features: (railsContext && railsContext.features) || props.features || [],
      };

  return (
    <ApolloProvider client={client}>
      <BuyerReadinessAssessmentInner {...props} company={mergedCompany} />
    </ApolloProvider>
  );
};

export default BuyerReadinessAssessmentContainer;
